import React, { useState, useMemo, useContext } from 'react';

import { getContactGender, getSortedValues, getVisibleMetricIndex, hiddenByGender } from '@libs/NwFeatures';

import { ListContext } from '@contexts/ListContext';
import { useFeaturesIndex, useGlobalFeatures, useGlobalConversions } from '@hooks/API/UseFeatures';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import EditFeatureDrawer from '@components/Features/EditFeatureDrawer';
import NwDivider from '@components/Gui/NwDivider';

import FeaturesGroupDesktopFeatureBox from './FeaturesGroupDesktopFeatureBox';
import { FeatureGroupContent } from './FeaturesGroupFeatureDisplay';
import { FeatureBoxContainer, FeatureContentWrapper, FeatureGroupContainer } from './FeaturesUI';
import {
    getHistoryTooltipContent,
    getItemSpan,
    getPublicFeatureForDisplay,
    shouldShowInlinePublicValue,
} from './FeaturesGroupUtils';

const FeaturesGroup = ({ featuresGroup, contactFeatures, contact, contactType, readOnly }) => {
    const featuresIndex = useFeaturesIndex();

    const { data: globalFeatures } = useGlobalFeatures();
    const { data: globalConversions } = useGlobalConversions();
    const [updatedFieldIDs, setUpdatedFieldIDs] = useState([]);
    const [selectedMetrics, setSelectedMetrics] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [editFeatureName, setEditFeatureName] = useState();
    const breakpoints = useNwBreakPoints();

    const { GetUserName } = useContext(ListContext);

    const changeMetric = (featureid, metricindex) => {
        const selectedMetricNoCurrent = selectedMetrics.filter((sm) => {
            return sm.FeatureID !== featureid;
        });
        setSelectedMetrics([...selectedMetricNoCurrent, { FeatureID: featureid, SelectedIndex: metricindex }]);
    };

    const groupFeatures = useMemo(() => {
        let groupFeatures = featuresGroup.GroupFeatures.map((groupFeature) => {
            let feature = globalFeatures && globalFeatures.find((feature) => feature.ID === groupFeature.FeatureID);
            if (feature && feature.TypeName === 'Conversion') {
                feature = {
                    ...feature,
                    conversion:
                        globalConversions &&
                        globalConversions.find((conversion) => conversion.ID === feature.ConversionID),
                };
            }
            return {
                ...groupFeature,
                Feature: feature,
            };
        });
        // stessa guardia della modifica: una feature non più fra quelle globali non ha nome né tipo
        return groupFeatures.filter(
            (groupFeature) => groupFeature.Feature && !hiddenByGender(groupFeature.Feature, contact)
        );
    }, [featuresGroup, globalFeatures, globalConversions, contact]);

    // il click sul valore apre la modifica come l'azione sul titolo del gruppo: entrambe vanno chiuse in sola lettura
    const handleEditFeatureContent = (featureName) => {
        if (readOnly) {
            return;
        }
        setEditFeatureName(featureName);
        setIsEdit(true);
    };

    const getMetricIndex = (itemFeature) => {
        if (selectedMetrics.length > 0) {
            const hasSelectedMetric = selectedMetrics.filter((obj) => {
                return obj.FeatureID === itemFeature.ID;
            });
            if (hasSelectedMetric.length > 0) {
                return hasSelectedMetric[0].SelectedIndex;
            }
        }
        return 0;
    };

    const gender = getContactGender(contact);

    const displayValueDeps = { featuresIndex, gender, getMetricIndex };

    const displayCtx = {
        breakpoints,
        featuresIndex,
        gender,
        getMetricIndex,
        onMetricChange: changeMetric,
        onEditFeature: handleEditFeatureContent,
    };

    const handleEdit = () => {
        setIsEdit(true);
    };

    return !globalFeatures || !globalConversions ? (
        <div />
    ) : (
        <React.Fragment key={featuresGroup.Name}>
            <FeatureGroupContainer>
                <NwDivider dark textSize="medium" action={!readOnly && handleEdit}>
                    {featuresGroup.Name}
                </NwDivider>
                {breakpoints === 1 ? (
                    <>
                        {groupFeatures.map((item) => {
                            const feature =
                                contactFeatures &&
                                contactFeatures.find((feature) => feature.FeatureID === item.FeatureID);
                            const isActive = updatedFieldIDs.find((fieldName) => fieldName === item.ID);
                            if (feature) {
                                const publicFeature = getPublicFeatureForDisplay(feature);
                                return (
                                    <FeatureContentWrapper
                                        key={item.ID}
                                        featureType={item.Feature.TypeName.toLowerCase()}
                                        isActive={isActive}
                                    >
                                        <div>{item.Feature.Name}</div>
                                        <FeatureGroupContent
                                            feature={feature}
                                            itemFeature={item.Feature}
                                            featureName={item.Feature.Name}
                                            {...displayCtx}
                                        />
                                        {/* valore pubblico accanto a quello privato, separati da bullet */}
                                        {publicFeature && (
                                            <>
                                                <span className="feature-inline-separator">&bull;</span>
                                                <FeatureGroupContent
                                                    feature={publicFeature}
                                                    itemFeature={item.Feature}
                                                    featureName={item.Feature.Name}
                                                    isPublicValue
                                                    {...displayCtx}
                                                />
                                            </>
                                        )}
                                    </FeatureContentWrapper>
                                );
                            }
                            return null;
                        })}
                    </>
                ) : (
                    <FeatureBoxContainer>
                        {groupFeatures.map((item) => {
                            const feature =
                                contactFeatures &&
                                contactFeatures.find((feature) => feature.FeatureID === item.FeatureID);
                            const isActive = updatedFieldIDs.find((fieldName) => fieldName === item.ID);
                            if (feature) {
                                const itemSpan = getItemSpan(feature, item.Feature);
                                const publicFeature = getPublicFeatureForDisplay(feature);
                                const showInlinePublicValue = shouldShowInlinePublicValue(
                                    feature,
                                    publicFeature,
                                    item.Feature,
                                    displayValueDeps
                                );
                                const conversionSortedValues =
                                    publicFeature &&
                                    item.Feature.TypeName.toLowerCase() === 'conversion' &&
                                    getSortedValues(feature.DisplayValue, item.Feature.ConversionID, featuresIndex, {
                                        gender,
                                    });
                                const conversionMetricIndex = getVisibleMetricIndex(
                                    conversionSortedValues || [],
                                    getMetricIndex(item.Feature)
                                );
                                return (
                                    <FeaturesGroupDesktopFeatureBox
                                        key={item.ID}
                                        item={item}
                                        feature={feature}
                                        isActive={isActive}
                                        itemSpan={itemSpan}
                                        publicFeature={publicFeature}
                                        showInlinePublicValue={showInlinePublicValue}
                                        conversionSortedValues={conversionSortedValues}
                                        conversionMetricIndex={conversionMetricIndex}
                                        historyTooltipTitle={getHistoryTooltipContent(feature, GetUserName)}
                                        ctx={displayCtx}
                                    />
                                );
                            }
                            return null;
                        })}
                    </FeatureBoxContainer>
                )}
            </FeatureGroupContainer>
            {isEdit && (
                <EditFeatureDrawer
                    featuresGroup={featuresGroup}
                    contactFeatures={contactFeatures}
                    contact={contact}
                    contactType={contactType}
                    editFeatureName={editFeatureName}
                    updatedFieldIDs={updatedFieldIDs}
                    setUpdatedFieldIDs={setUpdatedFieldIDs}
                    onClose={() => {
                        setIsEdit(false);
                        setEditFeatureName();
                    }}
                />
            )}
        </React.Fragment>
    );
};

export default FeaturesGroup;
